import React, { useEffect, useState } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';

interface LocationProps {
  title: string;
  description: string;
  images: string[];
  mapUrl: string;
  address: string;
}

const Location: React.FC<LocationProps> = ({ title, description, images, mapUrl, address }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-cycle images every 5 seconds
  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);





  const validImages = images.filter(img => img && img.trim() !== '');

  if (validImages.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-green-100 text-blue-800 px-6 py-3 rounded-full shadow-lg mb-8">
            <MapPin className="w-5 h-5 fill-current" />
            <span className="font-bold text-sm md:text-base">Nuestra Ubicación</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            {title || 'Dónde Encontrarnos'}
          </h2>

          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Visítanos en nuestra ubicación principal. Estamos aquí para atenderte con la mejor calidad y servicio.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Section */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
              <img
                src={validImages[currentImageIndex]}
                alt={title || 'Ubicación de Mundo Carne'}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />

              {validImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                  {validImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              )}

              <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1}/{validImages.length}
              </div>
            </div>


          </div>

          {/* Info Section */}
          <div className="space-y-6">
              <div className="bg-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Información de Ubicación</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {description || 'Estamos ubicados en el corazón de la ciudad, listos para servirte con los mejores productos cárnicos.'}
                </p>
                <div className="text-sm text-gray-600">
                  <strong>Dirección:</strong><br />
                  {address || 'Dirección no especificada'}
                </div>
              </div>

              {/* Map Section */}
              {(mapUrl || address) && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">Mapa de Ubicación</h4>
                    <button
                      onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank')}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver en Google Maps
                    </button>
                  </div>
                  <div className="relative h-64 bg-gray-100">
                    {(() => {
                      // Si hay una URL de embed personalizada válida, usarla
                      if (mapUrl && mapUrl.includes('embed') && mapUrl.includes('google.com/maps')) {
                        return (
                          <iframe
                            src={mapUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Mapa de ubicación"
                          />
                        );
                      }

                      // Si hay dirección, intentar generar un embed simple
                      if (address && address.trim() !== '') {
                        const firstLine = address.split('\n')[0].trim();
                        // Crear una URL simple de embed que funciona sin API key
                        const simpleEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(firstLine)}&output=embed`;

                        return (
                          <iframe
                            src={simpleEmbedUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Mapa de ubicación"
                            onError={() => {
                              // Fallback: mostrar vista previa estática
                            }}
                          />
                        );
                      }

                      // Fallback: vista previa estática con ubicación
                      return (
                        <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 to-green-50">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <MapPin className="w-8 h-8 text-blue-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">Ubicación</h4>
                            <p className="text-sm text-gray-600 mb-4 max-w-xs">
                              {address ? address.split('\n')[0] : 'Dirección no configurada'}
                            </p>
                            <div className="text-xs text-gray-500">
                              Haz clic en "Ver en Google Maps" para ver la ubicación completa
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </section>
  );
};

export default Location;